import React from "react";

import {
    Container,
    Box,
    Stack,
    Typography,
    Card,
    CardContent,
    CardActions,
    Switch,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Button,
    Grid
} from "@mui/material";

import { ConfigContext } from "../Config/ConfigContext";
import useForm from "../hooks/useForm";
import FormInput from "../form/form-input";
import http from "../utils/http";

import { capitalize, debounce, isArray } from "lodash";
import { Add, Delete } from "@mui/icons-material";
import EnvironmentDeleteModal from "./environmentDeleteModal";
import EnvironmentURIAddModal from "./environmentURIAddModal";
import EnvironmentURIDeleteModal from "./environmentURIDeleteModal";
import EnvironmentDomainAddModal from "./environmentDomainAddModal";
import EnvironmentDomainDeleteModal from "./environmentDomainDeleteModal";

const onLoad = async ({
    values,
}) => {
    const environment = await http.get({}, `${values.config.uri.base}/environment/${values._id}`);
    return {
        ...values,
        ...environment,
        loading: false,
    };
};

const saveField = debounce(async (data, uri) => {
    await http.put(data, uri);
}, 500);

export default function EnvironmentDetail({
    environmentId,
}) {
    const { config } = React.useContext(ConfigContext);
    // const [value, setValue] = React.useState(0);
    // const handleChange = (_, newValue) => {
    //     setValue(newValue);
    // };

    const {
        state,
        onChange,
        onChangeBulk,
        onInvalidForm,
    } = useForm({
        debug: true,
        initialState: {
            config,
            environmentAddModalIsOpen: false,
            environmentDeleteModalIsOpen: false,
            applicationURIAddModalIsOpen: false,
            applicationURIDeleteModalIsOpen: false,
            acceptedDomainAddModalIsOpen: false,
            acceptedDomainDeleteModalIsOpen: false,

            _id: environmentId,
            name: null,
            integrations: null,
            domains: [],
            uri: null,
            applicationId: null,
            tenantId: null,
            appId: null,
            createdAt: null,
            createdBy: null,
            updatedAt: null,
            updatedBy: null,
            deletedAt: null,
            deletedBy: null,
            deleted: false,
            loading: true
        },
        onLoad
    });

    return (
        <Box>
            {state.values.loading &&
                <Container>
                    Loading...
                </Container>
            }
            {!state.values.loading &&
                <Box sx={{p:0}}>
                    <EnvironmentURIAddModal
                        onChange={onChange}
                        onChangeBulk={onChangeBulk}
                        onInvalidForm={onInvalidForm}
                        config={config}
                        isOpen={state.values.applicationURIAddModalIsOpen}
                        environmentId={state.values._id}
                    />
                    <EnvironmentDomainAddModal
                        onChange={onChange}
                        onChangeBulk={onChangeBulk}
                        onInvalidForm={onInvalidForm}
                        config={config}
                        isOpen={state.values.acceptedDomainAddModalIsOpen}
                        environmentId={state.values._id}
                    />

                    <Card sx={{ marginTop: '20px', minWidth: 252 }}>
                        <CardContent>
                            <Typography variant="h5" component={'div'}>Name</Typography>
                            <FormInput
                                label='Environment Name'
                                path={`name`}
                                value={state.values.name}
                                onChange={(value, path) => {
                                    onChange(value, path);
                                    saveField({ [path]: value }, `${config.uri.base}/environment/${state.values._id}`)
                                }}
                            />
                        </CardContent>
                    </Card>

                    <Card sx={{ marginTop: '20px', minWidth: 252 }}>
                        <CardContent>
                            <Box sx={{ marginTop: '20px' }}>
                                <Stack direction={'row'} alignItems={'center'}>
                                    <Typography variant="h5" component={'div'}>Application URIs</Typography>
                                    <IconButton onClick={() => {
                                        onChange(true, 'applicationURIAddModalIsOpen');
                                    }}>
                                        <Add />
                                    </IconButton>
                                </Stack>
                                <Typography>Values that developer(s) will use in code to tie API URLs to features</Typography>

                                <Stack >
                                    {Object.keys(state.values.uri).map((uriKey, uriIndex) => {
                                        return (
                                            <Grid container spacing={2} key={uriIndex} alignItems="center" sx={{ width: '100%' }}>
                                                <Grid item xs={11}>
                                                    <FormInput
                                                        key={uriIndex}
                                                        variant="outlined"
                                                        label={`${capitalize(uriKey)} API`}
                                                        path={`uri.${uriKey}`}
                                                        value={state.values.uri[uriKey]}
                                                        onChange={(value, path) => {
                                                            onChange(value, path);
                                                            saveField({ [path]: value }, `${config.uri.base}/environment/${state.values._id}`);
                                                        }}
                                                        fullWidth
                                                    />
                                                </Grid>
                                                <Grid item xs={1}>
                                                    <IconButton onClick={() => onChange(true, 'applicationURIDeleteModalIsOpen')}>
                                                        <Delete />
                                                    </IconButton>
                                                </Grid>

                                                <EnvironmentURIDeleteModal
                                                    onChange={onChange}
                                                    onChangeBulk={onChangeBulk}
                                                    onInvalidForm={onInvalidForm}
                                                    config={config}
                                                    isOpen={state.values.applicationURIDeleteModalIsOpen}
                                                    applicationURIName={uriKey}
                                                    environmentId={state.values._id}
                                                />
                                            </Grid>

                                        )
                                    })}
                                </Stack>
                            </Box>
                        </CardContent>
                    </Card>
                    <Card sx={{ marginTop: '20px', minWidth: 252 }}>
                        <CardContent>
                            <Box sx={{ marginTop: '20px' }}>
                                <Stack direction={'row'} alignItems={'center'}>
                                    <Typography variant="h5" component={'div'}>Accepted Domains</Typography>
                                    <IconButton onClick={() => {
                                        onChange(true, 'acceptedDomainAddModalIsOpen');
                                    }}>
                                        <Add />
                                    </IconButton>
                                </Stack>
                                <Typography>COR(s) field controls how an environment is matched with a config</Typography>


                                <Stack direction={'row'} justifyContent={'space-between'} flexWrap={'wrap'}>
                                    {state.values.domains.map((domain, domainIndex) => {
                                        return (
                                            <Grid container spacing={2} key={domainIndex} alignItems="center" sx={{ width: '100%' }}>
                                                <Grid item xs={11}>
                                                    <FormInput
                                                        label="Supported Domains"
                                                        path={`domains[${domainIndex}]`}
                                                        value={state.values.domains[domainIndex]}
                                                        onChange={(value, path) => {
                                                            onChange(value, path);
                                                            saveField({ [path]: value }, `${config.uri.base}/environment/${state.values._id}`);
                                                        }}
                                                        fullWidth
                                                    />
                                                </Grid>
                                                <Grid item xs={1}>
                                                    <IconButton onClick={() => onChange(true, 'acceptedDomainDeleteModalIsOpen')}>
                                                        <Delete />
                                                    </IconButton>
                                                </Grid>

                                                <EnvironmentDomainDeleteModal
                                                    onChange={onChange}
                                                    onChangeBulk={onChangeBulk}
                                                    onInvalidForm={onInvalidForm}
                                                    config={config}
                                                    isOpen={state.values.acceptedDomainDeleteModalIsOpen}
                                                    acceptedDomain={domain}
                                                    environmentId={state.values._id}
                                                />
                                            </Grid>

                                        )
                                    })
                                    }
                                </Stack>

                            </Box>
                        </CardContent>
                    </Card>

                    <Box sx={{ marginTop: '10px' }}>
                        <Stack direction={'row'} alignItems={'center'}>
                            <Typography variant="h5" component={'div'}>Integrations</Typography>
                            <IconButton onClick={() => {

                            }}>
                                <Add />
                            </IconButton>
                        </Stack>

                        <Stack direction={'column'} justifyContent={'space-between'} flexWrap={'wrap'}>
                            {Object.keys(state.values.integrations).map((integrationKey, integrationIndex) => {
                                return (
                                    <Card key={integrationIndex} sx={{ marginTop: '20px', minWidth: 252 }}>
                                        <CardContent>
                                            <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
                                                {capitalize(integrationKey.replace(/([a-z0-9])([A-Z])/g, '$1 $2'))}
                                            </Typography>
                                            <Typography>
                                                Enabled
                                                <Switch
                                                    checked={state.values.integrations[integrationKey].enabled}
                                                    onChange={(event) => {
                                                        const path = `integrations[${integrationKey}].enabled`;
                                                        onChange(event.target.checked, path);
                                                        saveField({ [path]: event.target.checked }, `${config.uri.base}/environment/${state.values._id}`);
                                                    }}
                                                />
                                            </Typography>
                                            <Box sx={{ marginTop: '20px' }}>
                                                <Typography>Engine</Typography>
                                                <FormControl sx={{ marginTop: '10px' }}>
                                                    <InputLabel id="demo-simple-select-label">Engine</InputLabel>
                                                    <Select
                                                        labelId="demo-simple-select-label"
                                                        id="demo-simple-select"
                                                        value={state.values.integrations[integrationKey].engine}
                                                        label="Engine"
                                                    // onChange={handleChange}
                                                    >
                                                        {Object.keys(state.values.integrations[integrationKey].engineConfig).map((engineKey, engineIndex) => {
                                                            return (
                                                                <MenuItem key={engineIndex} value={engineKey}>{engineKey}</MenuItem>
                                                            );
                                                        })}
                                                    </Select>
                                                </FormControl>
                                                <Grid container spacing={2} key={integrationKey} flexWrap="wrap">
                                                    {Object.keys(state.values.integrations[integrationKey].engineConfig[state.values.integrations[integrationKey].engine])
                                                        .sort((a, b) => {
                                                            const engine = state.values.integrations[integrationKey].engine;
                                                            const typeA = typeof state.values.integrations[integrationKey].engineConfig[engine][a];
                                                            const typeB = typeof state.values.integrations[integrationKey].engineConfig[engine][b];

                                                            // Strings (and other non-objects) should appear first
                                                            if (typeA === 'string' && typeB !== 'string') return -1;
                                                            if (typeA !== 'string' && typeB === 'string') return 1;

                                                            // Keep objects at the bottom
                                                            if (typeA === 'object' && typeB !== 'object') return 1;
                                                            if (typeA !== 'object' && typeB === 'object') return -1;

                                                            // If types are the same, maintain original order
                                                            return 0;
                                                        })
                                                        .map((engineConfigKey, engineConfigIndex) => {

                                                            const engine = state.values.integrations[integrationKey].engine;
                                                            const val = state.values.integrations[integrationKey].engineConfig[engine][engineConfigKey];
                                                            const valType = typeof val;
                                                            const valIsArray = Array.isArray(val);

                                                            return (
                                                                <React.Fragment key={engineConfigIndex}>
                                                                    {valType === 'string' && (
                                                                        <Grid item xs={12}>
                                                                            <FormInput
                                                                                label={engineConfigKey}
                                                                                path={`integrations[${integrationKey}].engineConfig[${engine}][${engineConfigKey}]`}
                                                                                value={state.values.integrations[integrationKey].engineConfig[engine][engineConfigKey]}
                                                                                onChange={(value, path) => {
                                                                                    onChange(value, path);
                                                                                    saveField({ [path]: value }, `${config.uri.base}/environment/${state.values._id}`);
                                                                                }}
                                                                                fullWidth
                                                                            />
                                                                        </Grid>
                                                                    )}
                                                                    {!valIsArray && valType === 'object' && (
                                                                        <Grid item xs={12}>
                                                                            <Box sx={{ border: '1px solid #ccc', borderRadius: '4px', padding: '16px' }}>
                                                                                <Grid container spacing={2} alignItems="center">
                                                                                    {Object.keys(val)
                                                                                        .sort((a, b) => {
                                                                                            // Sort "Enabled" field first
                                                                                            if (a.toLowerCase() === 'enabled') return -1;
                                                                                            if (b.toLowerCase() === 'enabled') return 1;

                                                                                            // For all other fields, maintain the original order
                                                                                            return 0;
                                                                                        })
                                                                                        .map((nestedEngineObjectKey, nestedEngineObjectIndex) => {

                                                                                            const nestedEngineKeyVal = state.values.integrations[integrationKey].engineConfig[engine][engineConfigKey][nestedEngineObjectKey];
                                                                                            const nestedEngineValType = typeof nestedEngineKeyVal;
                                                                                            return (
                                                                                                <React.Fragment key={nestedEngineObjectIndex}>
                                                                                                    <Grid item xs={nestedEngineValType === 'boolean' ? 1 : 11}>
                                                                                                        {nestedEngineValType === 'boolean' && (
                                                                                                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                                                                                                <Typography>{engineConfigKey}</Typography>
                                                                                                                <Switch
                                                                                                                    checked={state.values.integrations[integrationKey].engineConfig[engine][engineConfigKey][nestedEngineObjectKey]}
                                                                                                                    onChange={(event) => {
                                                                                                                        const path = `integrations[${integrationKey}].engineConfig[${engine}][${engineConfigKey}][${nestedEngineObjectKey}]`;
                                                                                                                        onChange(event.target.checked, path);
                                                                                                                        saveField({ [path]: event.target.checked }, `${config.uri.base}/environment/${state.values._id}`);
                                                                                                                    }}
                                                                                                                />
                                                                                                            </Box>
                                                                                                        )}
                                                                                                    </Grid>
                                                                                                    {nestedEngineValType === 'string' && (
                                                                                                        <Grid item xs={nestedEngineValType === 'boolean' ? 11 : 12}>
                                                                                                            <FormInput
                                                                                                                label={nestedEngineObjectKey}
                                                                                                                path={`integrations[${integrationKey}].engineConfig[${engine}][${engineConfigKey}][${nestedEngineObjectKey}]`}
                                                                                                                value={state.values.integrations[integrationKey].engineConfig[engine][engineConfigKey][nestedEngineObjectKey]}
                                                                                                                onChange={(value, path) => {
                                                                                                                    onChange(value, path);
                                                                                                                    saveField({ [path]: value }, `${config.uri.base}/environment/${state.values._id}`);
                                                                                                                }}
                                                                                                                fullWidth
                                                                                                            />
                                                                                                        </Grid>
                                                                                                    )}
                                                                                                </React.Fragment>
                                                                                            );
                                                                                        })}
                                                                                </Grid>
                                                                            </Box>
                                                                        </Grid>
                                                                    )}
                                                                    {valIsArray && val.map((item, itemIndex) => (
                                                                        <Grid item xs={12} key={itemIndex}>
                                                                            <Typography>{engineConfigKey}</Typography>
                                                                            <Grid container spacing={2}>
                                                                                {Object.keys(item).map((itemKey) => (
                                                                                    <Grid item xs={12} key={itemKey}>
                                                                                        <FormInput
                                                                                            label={itemKey}
                                                                                            path={`integrations[${integrationKey}].engineConfig[${engine}][${engineConfigKey}][${itemIndex}][${itemKey}]`}
                                                                                            value={state.values.integrations[integrationKey].engineConfig[engine][engineConfigKey][itemIndex][itemKey]}
                                                                                            onChange={(value, path) => {
                                                                                                onChange(value, path);
                                                                                                saveField({ [path]: value }, `${config.uri.base}/environment/${state.values._id}`);
                                                                                            }}
                                                                                            fullWidth
                                                                                        />
                                                                                    </Grid>
                                                                                ))}
                                                                            </Grid>
                                                                        </Grid>
                                                                    ))}
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                </Grid>

                                            </Box>
                                        </CardContent>
                                        <CardActions>
                                        </CardActions>
                                    </Card>
                                )
                            })

                            }
                        </Stack>
                    </Box>
                    <Button variant="contained" onClick={() => onChange(true, 'environmentDeleteModalIsOpen')}>
                        DELETE <Delete />
                    </Button>
                    <EnvironmentDeleteModal
                        onChange={onChange}
                        onChangeBulk={onChangeBulk}
                        onInvalidForm={onInvalidForm}
                        config={config}
                        isOpen={state.values.environmentDeleteModalIsOpen}
                        environmentId={state.values._id}
                        environmentName={state.values.name}
                        applicationId={state.values.applicationId}
                    />
                </Box>}
        </Box>
    );
}
